import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    GridFilterModel,
    GridRenderCellParams,
    GridRowParams,
    GridSelectionModel,
    GridSortItem,
    GridSortModel,
} from '@mui/x-data-grid';
import { Add, LocalShipping } from '@mui/icons-material';
import { Device, NapiResultType } from 'types';
import { OpenModalButton } from 'components/button/OpenModalButton/OpenModalButton';
import { DataTable, DataTableProps } from 'components/data/DataTable/DataTable';
import { Sparkline } from 'components/data/Sparklines/Sparkline';
import { CreateDeviceModal } from 'components/modals/CreateDeviceModal/CreateDeviceModal';
import {
    BulkEditDevicesModalButton,
    BulkEditDevicesModalButtonProps,
} from '../BulkEditDevicesModal/BulkEditDevicesModal';
import { getDeviceColumns } from 'data-structures/sharedColumns/deviceColumns';
import styles from './TanksListView.module.scss';
import { useLazyGetSupplierDevicesEsQuery } from 'store/services';
import { DataTableReportPreset } from 'components/data/DataTable/components/GridToolbarPresetSelector/GridToolbarPresetSelector';
import { ElasticsearchDeviceBodyConfig } from 'types/ElasticsearchQueries';
import { CreateBulkFillupsModal } from '../CreateBulkFillupsModal/CreateBulkFillupsModal';

export interface TanksListViewProps {
    data?: NapiResultType<Device[]>;
    loading: boolean;
    fetching: boolean;
    onFilterModelChange: React.Dispatch<
        React.SetStateAction<GridFilterModel | undefined>
    >;
    onPageChange: React.Dispatch<React.SetStateAction<number>>;
    onPageSizeChange: React.Dispatch<React.SetStateAction<number>>;
    onSortModelChange: React.Dispatch<React.SetStateAction<GridSortItem[]>>;
    onCurrentPresetChange: React.Dispatch<
        React.SetStateAction<DataTableReportPreset | null>
    >;
    gridSortModel: GridSortModel;
    page: number;
    viewAsSupplier?: string;
    queryConfig?: ElasticsearchDeviceBodyConfig;
    /** When device is added use set state callback to alert parent component to refetch devices. */
    onDeviceCreated?: React.Dispatch<React.SetStateAction<boolean>>;
    /** When device is added use set state callback to alert parent component what device id was created. */
    setCreatedDeviceId: React.Dispatch<React.SetStateAction<string>>;
    // callback to refetch device list data from child components
    refetchListView: React.Dispatch<React.SetStateAction<boolean>>;
}

export function TanksListView({
    data,
    loading,
    fetching,
    onFilterModelChange,
    onPageChange,
    onPageSizeChange,
    onSortModelChange,
    onCurrentPresetChange,
    gridSortModel,
    page,
    viewAsSupplier,
    queryConfig,
    onDeviceCreated,
    setCreatedDeviceId,
    refetchListView,
}: TanksListViewProps) {
    const navigate = useNavigate();
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [query] = useLazyGetSupplierDevicesEsQuery();
    const [selectionModel, setSelectionModel] = useState<GridSelectionModel>(
        []
    );

    const onRowClick = (params: GridRowParams<any>) => {
        navigate(`/device-details/${params.row.deviceId}`, {
            state: params?.row,
        });
    };
    const filterModelChangeInternal = (event: GridFilterModel | undefined) => {
        if (event?.quickFilterValues && event.quickFilterValues.length > 0)
            setIsSearchActive(true);
        else setIsSearchActive(false);
        onFilterModelChange(event);
    };

    const [deviceColumns, setDeviceColumns] = useState<any[]>([]);

    useMemo(() => {
        setDeviceColumns(getDeviceColumns({ refetchListView }));
    }, [refetchListView]);

    const BulkEditModal = useMemo(() => {
        // eslint-disable-next-line react/display-name
        return (props: BulkEditDevicesModalButtonProps) => (
            <BulkEditDevicesModalButton
                supplierId={viewAsSupplier}
                queryConfig={queryConfig}
                refetchQuery={() => refetchListView(true)}
                {...props}
            />
        );
    }, [queryConfig, refetchListView, viewAsSupplier]);

    return (
        <div data-testid="TanksListView" className={styles.container}>
            <DataTable
                id="device"
                data={data}
                columns={deviceColumns}
                loading={loading || fetching}
                presets={tankColumnPresets}
                setCustomSelectionModel={setSelectionModel}
                customSelectionModel={selectionModel}
                toolbarComponents={
                    <>
                        <OpenModalButton
                            icon={<LocalShipping />}
                            modal={CreateBulkFillupsModal}
                            className={styles.openModal}
                            modalProps={{
                                devices: selectionModel,
                                setDevices: setSelectionModel,
                                supplierId: viewAsSupplier,
                            }}
                            disabled={!selectionModel.length}
                        >
                            Report Deliveries
                        </OpenModalButton>
                        <OpenModalButton
                            icon={<Add />}
                            modal={CreateDeviceModal}
                            className={styles.openModal}
                            modalProps={{
                                supplierId: viewAsSupplier,
                                onDeviceCreated,
                                setCreatedDeviceId,
                            }}
                        >
                            Deploy
                        </OpenModalButton>
                    </>
                }
                onRowClick={onRowClick}
                bulkEditModal={(props) => <BulkEditModal {...props} />}
                getRowId={(row: Device) => row.deviceId ?? JSON.stringify(row)}
                onFilterModelChange={filterModelChangeInternal}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                onSortModelChange={onSortModelChange}
                onCurrentPresetChange={onCurrentPresetChange}
                filterMode="server"
                paginationMode="server"
                sortingMode="server"
                autoFocusSearch={isSearchActive}
                csvOptions={{
                    query,
                    // Mostly copying from the queryConfig but we don't want to include any offset/limit info because we want to export whole collection.
                    queryConfig: {
                        ...queryConfig,
                        ...{
                            limit: undefined,
                            offset: undefined,
                        },
                    },
                    fileName: 'monitors',
                    supplierId: viewAsSupplier,
                }}
                hideSearch={true}
                sortModel={gridSortModel}
                page={page}
            />
        </div>
    );
}

export const sparklineRenderCell = (params: GridRenderCellParams) => {
    const data = params.value ? params.value.split(',') : [];
    return <Sparkline data={data} />;
};

const tankColumnPresets: DataTableProps['presets'] = [
    {
        name: 'Estimated Fill',
        cols: [
            'health',
            'lastReading.tank',
            'duf_7',
            'l30',
            'c_avg_7',
            'p_avg_7',
            'customer_name',
            'name',
        ],
        sort: {
            order: 'desc',
            by: 'duf_7',
        },
    },
    {
        name: 'Low Tanks',
        cols: [
            'health',
            'lastReading.tank',
            'lastReading.time',
            'customer_name',
            'name',
        ],
        sort: {
            order: 'asc',
            by: 'lastReading.tank',
        },
    },
    {
        name: 'Offline Monitors',
        cols: [
            'health',
            'lastReading.time',
            'lastReading.tank',
            'customer_name',
            'name',
            'lastReading',
        ],
        sort: {
            order: 'desc',
            by: 'lastReading.time',
        },
    },
    {
        name: 'Backoffice Sync',
        cols: ['health', 'customer_name', 'name', 'accountId', 'bo_sync_time'],
        sort: {
            order: 'desc',
            by: 'bo_sync_time',
        },
    },
    {
        name: 'Ullage',
        cols: [
            'health',
            'lastReading.tank',
            'ullage',
            'duf_7',
            'l30',
            'customer_name',
            'name',
        ],
        sort: {
            order: 'asc',
            by: 'ullage',
        },
    },
];
